import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Coins, Save, ToggleLeft, ToggleRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ImageStudioPricing() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // model -> { credits_per_image }
  const [savingModel, setSavingModel] = useState(null);

  const fetchPricing = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/admin/image-studio-pricing/`, { withCredentials: true });
      setRows(res.data.pricing || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to fetch pricing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      toast.error("Admin access required");
      navigate("/dashboard");
      return;
    }
    fetchPricing();
  }, [isAdmin, navigate]);

  const updateDraft = (model, value) => {
    setDrafts((d) => ({ ...d, [model]: { ...(d[model] || {}), credits_per_image: value } }));
  };

  const savePricing = async (model) => {
    const draft = drafts[model];
    if (!draft || draft.credits_per_image === undefined) return;
    const value = parseFloat(draft.credits_per_image);
    if (isNaN(value) || value < 0) {
      toast.error("Credits per image must be a non-negative number");
      return;
    }
    try {
      setSavingModel(model);
      await axios.patch(`${API}/api/admin/image-studio-pricing/${model}`, { credits_per_image: value }, { withCredentials: true });
      toast.success(`${model}: updated to ${value} credits`);
      setDrafts((d) => { const n = { ...d }; delete n[model]; return n; });
      await fetchPricing();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSavingModel(null);
    }
  };

  const toggleEnabled = async (model, currentEnabled) => {
    try {
      setSavingModel(model);
      await axios.patch(`${API}/api/admin/image-studio-pricing/${model}`, { enabled: !currentEnabled }, { withCredentials: true });
      toast.success(`${model}: ${!currentEnabled ? "Enabled" : "Disabled"}`);
      await fetchPricing();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setSavingModel(null);
    }
  };

  const saveMaxRefs = async (model, value) => {
    if (value < 0 || value > 14) {
      toast.error("Max references must be between 0 and 14");
      return;
    }
    try {
      setSavingModel(model);
      await axios.patch(`${API}/api/admin/image-studio-pricing/${model}`, { max_reference_count: value }, { withCredentials: true });
      toast.success(`${model}: max refs = ${value}`);
      await fetchPricing();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingModel(null);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-6" data-testid="admin-image-studio-pricing-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center">
            <Coins className="w-5 h-5 text-zinc-950" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Image Studio Pricing</h1>
            <p className="text-sm text-zinc-400">Configure per-model credit cost and availability.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden" data-testid="image-studio-pricing-table">
            <table className="w-full">
              <thead className="bg-zinc-950 border-b border-zinc-800 text-xs text-zinc-400 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Model</th>
                  <th className="text-left px-4 py-3">Provider</th>
                  <th className="text-left px-4 py-3">Credits</th>
                  <th className="text-left px-4 py-3">Aspects</th>
                  <th className="text-left px-4 py-3">Resolutions</th>
                  <th className="text-left px-4 py-3">Max refs</th>
                  <th className="text-left px-4 py-3">Enabled</th>
                  <th className="text-left px-4 py-3">Last update</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {rows.map((r) => {
                  const draft = drafts[r.model];
                  const dirty = draft?.credits_per_image !== undefined && parseFloat(draft.credits_per_image) !== r.credits_per_image;
                  return (
                    <tr key={r.model} className="border-b border-zinc-800 last:border-0" data-testid={`pricing-row-${r.model}`}>
                      <td className="px-4 py-3 font-medium">{r.display_name}<div className="text-[10px] text-zinc-500">{r.model}</div></td>
                      <td className="px-4 py-3 text-zinc-400">{r.provider}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            step="0.5"
                            value={draft?.credits_per_image ?? r.credits_per_image}
                            onChange={(e) => updateDraft(r.model, e.target.value)}
                            className="w-20 bg-zinc-950 border border-zinc-700 rounded px-2 py-1 text-sm focus:outline-none focus:border-amber-500/50"
                            data-testid={`pricing-credits-input-${r.model}`}
                          />
                          <Button
                            size="sm"
                            disabled={!dirty || savingModel === r.model}
                            onClick={() => savePricing(r.model)}
                            className="bg-amber-400 hover:bg-amber-300 text-zinc-950 disabled:opacity-30"
                            data-testid={`pricing-save-${r.model}`}
                          >
                            {savingModel === r.model ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                          </Button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-400">{r.supported_aspects?.join(", ")}</td>
                      <td className="px-4 py-3 text-xs text-zinc-400">
                        <div>{(r.supported_resolutions || []).join(", ") || "—"}</div>
                        {r.default_resolution && <div className="text-[10px] text-zinc-600">default: {r.default_resolution}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="0"
                          max="14"
                          value={draft?.max_reference_count ?? r.max_reference_count ?? 0}
                          onChange={(e) => updateDraft(r.model, { ...drafts[r.model], max_reference_count: e.target.value })}
                          onBlur={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!isNaN(v) && v !== r.max_reference_count) {
                              saveMaxRefs(r.model, v);
                            }
                          }}
                          className="w-16 bg-zinc-950 border border-zinc-700 rounded px-2 py-1 text-sm focus:outline-none focus:border-amber-500/50"
                          data-testid={`pricing-max-refs-${r.model}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleEnabled(r.model, r.enabled)}
                          disabled={savingModel === r.model}
                          className={`flex items-center gap-1.5 px-2 py-1 rounded ${r.enabled ? "text-emerald-300 hover:bg-emerald-500/10" : "text-zinc-500 hover:bg-zinc-800"} disabled:opacity-40`}
                          data-testid={`pricing-toggle-${r.model}`}
                        >
                          {r.enabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                          {r.enabled ? "Enabled" : "Disabled"}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-[11px] text-zinc-500">
                        {r.updated_at && new Date(r.updated_at).toLocaleString()}
                        {r.updated_by && <div className="text-[10px] text-zinc-600">by {r.updated_by}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
